import type { SvgIconComponent } from "@mui/icons-material";
import NightsStayOutlinedIcon from "@mui/icons-material/NightsStayOutlined";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import WbTwilightIcon from "@mui/icons-material/WbTwilight";
import { SvgIcon, type SvgIconProps } from "@mui/material";
import type { FC, ReactNode } from "react";
import type { ShiftPeriod } from "../../../utils/shiftPeriod";

/**
 * Sun / twilight / moon glyphs for the three `ShiftPeriod`s — the icon half of Management Center's
 * `SHIFT_TYPE_COLORS`, previously duplicated as an identical `SHIFT_ICONS` map in both
 * `ShiftTypeColumn` and `ShiftHoursSelect`.
 */
const PERIOD_ICONS: Record<ShiftPeriod, SvgIconComponent> = {
	Day: WbSunnyOutlinedIcon,
	Evening: WbTwilightIcon,
	Night: NightsStayOutlinedIcon,
};

/** The filled glyphs' own canvas: a 16 × 17 box, a touch taller than wide, so the horizon line sits under the half sun. */
const FILLED_VIEW_BOX = "0 0 16 17";

/**
 * The filled glyphs, in `currentColor`: a disc with eight rays, a half sun on the horizon with three rays, and a solid
 * crescent. Shapes, not outlines, so they hold their colour at 16px beside text.
 */
const FILLED_GLYPHS: Record<ShiftPeriod, ReactNode> = {
	Day: (
		<>
			<circle cx="8" cy="8.5" r="3.6" fill="currentColor" />
			<path
				d="M8 .9v1.8M8 14.3v1.8M15.1 8.5h-1.8M2.7 8.5H.9M13.02 3.48l-1.27 1.27M4.25 12.25l-1.27 1.27M13.02 13.52l-1.27-1.27M4.25 4.75L2.98 3.48"
				fill="none"
				stroke="currentColor"
				strokeWidth={1.5}
				strokeLinecap="round"
			/>
		</>
	),
	Evening: (
		<>
			<path d="M4.6 11.2a3.4 3.4 0 016.8 0z" fill="currentColor" />
			<path d="M8 2.6v1.6M13 4.9l-1.1 1.1M2.9 4.9L4 6M1.2 11.2h1.6M13.2 11.2h1.6" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" />
			<path d="M1.2 14.1h13.6" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" />
		</>
	),
	Night: <path d="M13.4 10.9A6 6 0 016.1 3.1a6.2 6.2 0 102.3 12 6.2 6.2 0 005-4.2z" fill="currentColor" />,
};

/**
 * `outlined` — MUI's outlined sun, twilight and moon-with-stars: Management Center's look, and every caller's that
 * doesn't ask.
 * `filled` — solid shapes (`FILLED_GLYPHS`), for a dense surface where the glyph marks a figure or a time and an
 * outline thins out at 16px (the Locations page's rows, bands and building strip).
 */
export type ShiftPeriodIconGlyph = "outlined" | "filled";

type Props = {
	period: ShiftPeriod;
	/** Icon ink; paints in `currentColor` (MUI's icon default) when omitted. Pass the caller's own
	 * color token — e.g. `SHIFT_TYPE_COLORS[period].text` or `SHIFT_PERIOD[period].INK`. */
	color?: SvgIconProps["htmlColor"];
	fontSize?: SvgIconProps["fontSize"];
	sx?: SvgIconProps["sx"];
	/** DEFAULT: `outlined` — the glyphs every caller drew before `filled` existed. */
	glyph?: ShiftPeriodIconGlyph;
	/** Set only when this glyph stands alone with no adjacent text naming the period (it then gets an
	 * accessible name and MUI drops `aria-hidden`). Leave unset — the default — when the period is
	 * already named beside the icon, which is every current call site; the glyph stays decorative. */
	titleAccess?: string;
	dataTestId?: string;
};

/** Glyph-only icon for a `ShiftPeriod` (`utils/shiftPeriod.ts`). Renders nothing but the icon —
 * callers own color and any adjacent label. */
export const ShiftPeriodIcon: FC<Props> = ({ period, color, fontSize, sx, glyph = "outlined", titleAccess, dataTestId }) => {
	if (glyph === "filled") {
		return (
			<SvgIcon viewBox={FILLED_VIEW_BOX} htmlColor={color} fontSize={fontSize} sx={sx} titleAccess={titleAccess} data-testid={dataTestId}>
				{FILLED_GLYPHS[period]}
			</SvgIcon>
		);
	}
	const Icon = PERIOD_ICONS[period];
	return <Icon htmlColor={color} fontSize={fontSize} sx={sx} titleAccess={titleAccess} data-testid={dataTestId} />;
};
